import React, { useState } from 'react';
import {
  Box, Card, CardContent, TextField, Button, Typography, Alert, InputAdornment, IconButton, CircularProgress
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';

interface LoginFormProps {
  onLogin: (token: string, user: any) => void;
}

const LoginForm: React.FC<LoginFormProps> = ({ onLogin }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
      });
      const data = await response.json();
      if (data.success) {
        localStorage.setItem('auth_token', data.token);
        localStorage.setItem('auth_user', JSON.stringify(data.user));
        onLogin(data.token, data.user);
      } else {
        setError(data.error || 'Credenziali non valide');
      }
    } catch (err) {
      setError('Errore di connessione al server');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#0B0F1A', position: 'relative', overflow: 'hidden' }}>
      {/* Orb animati */}
      <Box sx={{ position: 'absolute', width: 400, height: 400, borderRadius: '50%', background: '#6366F1', filter: 'blur(240px)', opacity: 0.15, top: '-10%', left: '-5%', animation: 'orbs 20s ease-in-out infinite' }} />
      <Box sx={{ position: 'absolute', width: 300, height: 300, borderRadius: '50%', background: '#EC4899', filter: 'blur(180px)', opacity: 0.12, bottom: '-5%', right: '-3%', animation: 'orbs 20s ease-in-out 7s infinite' }} />
      <Box sx={{ position: 'absolute', width: 350, height: 350, borderRadius: '50%', background: '#06B6D4', filter: 'blur(210px)', opacity: 0.1, top: '40%', right: '20%', animation: 'orbs 20s ease-in-out 14s infinite' }} />

      <Card sx={{ maxWidth: 420, width: '100%', mx: 3, position: 'relative', zIndex: 1, borderRadius: 5, backgroundColor: 'rgba(17, 24, 39, 0.72)', backdropFilter: 'blur(24px)', border: '1px solid rgba(148, 163, 184, 0.1)', boxShadow: '0 25px 50px rgba(0, 0, 0, 0.25)', animation: 'fadeInUp 600ms ease forwards' }}>
        <CardContent sx={{ p: { xs: 4, sm: 5 } }}>
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography variant="h3" sx={{ fontWeight: 700, color: '#F1F5F9', letterSpacing: '-0.02em', lineHeight: 1 }}>
              BiSolution
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 600, background: 'linear-gradient(135deg, #6366F1, #06B6D4)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text', mt: 0.5 }}>
              Analytics
            </Typography>
            <Typography variant="caption" sx={{ color: '#64748B', display: 'block', mt: 1.5 }}>
              Accedi alla dashboard
            </Typography>
          </Box>

          {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

          <form onSubmit={handleSubmit}>
            <TextField fullWidth label="Username" value={username} onChange={(e) => setUsername(e.target.value)} sx={{ mb: 2.5 }} required disabled={loading} autoFocus autoComplete="username" />
            <TextField
              fullWidth label="Password" type={showPassword ? 'text' : 'password'} value={password}
              onChange={(e) => setPassword(e.target.value)} sx={{ mb: 3 }} required disabled={loading} autoComplete="current-password"
              InputProps={{ endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowPassword(!showPassword)} edge="end" size="small" sx={{ color: '#64748B' }} disabled={loading}>
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              )}}
            />
            <Button fullWidth type="submit" variant="contained" disabled={loading || !username || !password} sx={{ height: 48, borderRadius: 3, fontSize: '1rem' }}>
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Accedi'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};

export default LoginForm;
